import React, { useEffect, useState } from "react";
import { X, Share } from "lucide-react";
import { PlatformIcon, PlatformWordmark } from "@/components/brand/PlatformWordmark";
/**
 * Bottom-anchored install prompt.
 *
 * Two flows, one component:
 *   • Android / Chrome / Edge: capture `beforeinstallprompt`, show a native
 *     "Install AIFLedger" CTA that fires `deferredPrompt.prompt()`.
 *   • iOS Safari: no programmatic prompt exists — show the well-known
 *     "Tap ⎦↑⎣ then Add to Home Screen" hint instead.
 *
 * Suppressed if the app is already running standalone (installed), if the
 * user has dismissed it in the last 7 days, or on non-touch desktops.
 *
 * Auto-hides after a successful install (Chrome fires `appinstalled`).
 */

const DISMISS_KEY = "aif_install_dismissed_at";
const SEVEN_DAYS_MS = 7 * 24 * 60 * 60 * 1000;

function isRunningStandalone() {
  if (typeof window === "undefined") return false;
  return (
    window.matchMedia?.("(display-mode: standalone)")?.matches ||
    // iOS-specific flag; kept behind chained optional to survive typing lint
    window.navigator.standalone === true
  );
}

function isIOS() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || navigator.vendor || "";
  return /iPad|iPhone|iPod/.test(ua) && !window.MSStream;
}

function isMobileViewport() {
  if (typeof window === "undefined") return false;
  return window.matchMedia?.("(max-width: 820px)")?.matches;
}

function recentlyDismissed() {
  try {
    const at = Number(localStorage.getItem(DISMISS_KEY) || 0);
    return at && Date.now() - at < SEVEN_DAYS_MS;
  } catch { return false; }
}

export default function InstallPWABanner() {
  const [deferred, setDeferred] = useState(null);   // BeforeInstallPromptEvent
  const [visible,  setVisible]  = useState(false);
  const [iosHint,  setIosHint]  = useState(false);

  useEffect(() => {
    if (isRunningStandalone() || recentlyDismissed()) return;

    // Chrome / Android / Edge / Samsung
    const onBIP = (e) => {
      e.preventDefault();
      setDeferred(e);
      // Give React one tick + a couple seconds so we don't fight first paint.
      setTimeout(() => setVisible(true), 2200);
    };
    window.addEventListener("beforeinstallprompt", onBIP);

    // Chrome fires this after the user completes install — hide the banner.
    const onInstalled = () => {
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
      setDeferred(null);
      setVisible(false);
    };
    window.addEventListener("appinstalled", onInstalled);

    // iOS Safari doesn't fire beforeinstallprompt. Show a hint after 6s,
    // and only if the user is on a mobile viewport (i.e. actually could
    // add to home screen). Desktop Safari users won't see anything.
    let iosTimer = null;
    if (isIOS() && isMobileViewport()) {
      iosTimer = setTimeout(() => { setIosHint(true); setVisible(true); }, 6000);
    }

    return () => {
      window.removeEventListener("beforeinstallprompt", onBIP);
      window.removeEventListener("appinstalled", onInstalled);
      if (iosTimer) clearTimeout(iosTimer);
    };
  }, []);

  const dismiss = () => {
    try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
    setVisible(false);
  };

  const install = async () => {
    if (!deferred) return;
    try {
      deferred.prompt();
      const choice = await deferred.userChoice;
      // Whatever the user picks, don't nag again for a week.
      try { localStorage.setItem(DISMISS_KEY, String(Date.now())); } catch { /* ignore */ }
      setDeferred(null);
      setVisible(false);
      if (choice?.outcome === "accepted" && window.gtag) {
        window.gtag("event", "pwa_installed", { source: "custom_banner" });
      }
    } catch {
      dismiss();
    }
  };

  if (!visible || (!deferred && !iosHint)) return null;

  return (
    <div
      className="fixed bottom-4 left-4 right-4 z-40 md:left-auto md:right-6 md:bottom-6 md:max-w-sm"
      data-testid="pwa-install-banner"
      role="dialog"
      aria-label="Install AIFLedger to your home screen"
    >
      <div className="relative bg-[#0b192c] text-white rounded-xl shadow-2xl border border-[#1a2942]
                      px-4 py-3.5 sm:px-5 sm:py-4">
        <button
          type="button"
          onClick={dismiss}
          aria-label="Dismiss install prompt"
          data-testid="pwa-install-dismiss"
          className="absolute top-2.5 right-2.5 text-white/50 hover:text-white transition-colors"
        >
          <X size={16} />
        </button>

        <div className="flex items-center gap-3">
          <PlatformIcon className="w-10 h-10 rounded-lg flex-shrink-0 bg-white/5 p-0.5" width={40} height={40} />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 leading-none">
              <span className="font-serif text-lg text-white">Install</span>
              <span className="bg-white rounded px-1 py-0.5 inline-block">
                <PlatformWordmark className="h-4" />
              </span>
            </div>
            <div className="text-[11px] text-white/60 mt-0.5 leading-snug">
              {iosHint
                ? "One-tap access, full-screen, works offline."
                : "One-tap access from your home screen — full-screen, no browser bar."}
            </div>
          </div>
        </div>

        {iosHint ? (
          <div className="mt-3 flex items-center gap-2 text-[12px] text-white/80 bg-white/5 rounded-lg px-3 py-2.5 leading-snug">
            <span>Tap</span>
            <Share size={15} className="text-[#c4a454] flex-shrink-0" />
            <span>then <b>Add to Home Screen</b>.</span>
          </div>
        ) : (
          <div className="mt-3 flex items-center gap-2">
            <button
              type="button"
              onClick={install}
              data-testid="pwa-install-accept"
              className="flex-1 bg-[#c4a454] hover:bg-[#d1b064] text-[#0b192c] font-semibold
                         text-sm py-2 rounded-md transition-colors"
            >
              Install
            </button>
            <button
              type="button"
              onClick={dismiss}
              className="text-white/60 hover:text-white text-sm py-2 px-3 transition-colors"
            >
              Not now
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
